import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import LocaleLayout from "@/components/LocaleLayout";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import DeferredUI from "@/components/DeferredUI";
import { lazy, Suspense } from "react";
import ScrollToTop from "@/components/ScrollToTop";

// All pages including Index are now lazy so the initial chunk stays tiny.
// HeroSection (LCP) is rendered from the inline shell in index.html, so the
// brief Suspense fallback while Index loads doesn't delay LCP.
const Index = lazy(() => import("./pages/Index"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const AchievementsPage = lazy(() => import("./pages/AchievementsPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogArticlePage = lazy(() => import("./pages/BlogArticlePage"));
const LecturesPage = lazy(() => import("./pages/LecturesPage"));
const PartnersPage = lazy(() => import("./pages/PartnersPage"));
const ZebbiePage = lazy(() => import("./pages/ZebbiePage"));
const CommunityPage = lazy(() => import("./pages/CommunityPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const SignupPage = lazy(() => import("./pages/SignupPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminArticles = lazy(() => import("./pages/admin/AdminArticles"));
const AdminPartners = lazy(() => import("./pages/admin/AdminPartners"));
const AdminMedia = lazy(() => import("./pages/admin/AdminMedia"));
const AdminAchievements = lazy(() => import("./pages/admin/AdminAchievements"));
const AdminLectures = lazy(() => import("./pages/admin/AdminLectures"));
const AdminCategories = lazy(() => import("./pages/admin/AdminCategories"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const SalesPage = lazy(() => import("./pages/SalesPage"));
const AdminInquiries = lazy(() => import("./pages/admin/AdminInquiries"));
const AdminPopupAnalytics = lazy(() => import("./pages/admin/AdminPopupAnalytics"));
const AdminEmailDeliverability = lazy(() => import("./pages/admin/AdminEmailDeliverability"));
const AdminInquiryCampaign = lazy(() => import("./pages/admin/AdminInquiryCampaign"));
const AdminAnalyticsCheck = lazy(() => import("./pages/admin/AdminAnalyticsCheck"));
const AdminCampaigns = lazy(() => import("./pages/admin/AdminCampaigns"));
const UnsubscribePage = lazy(() => import("./pages/UnsubscribePage"));

// AuthProvider + QueryClient pull in supabase + react-query + framer-motion
// (~95 KB gzip across `data` + `proxy` chunks). DeferredAuthProviders mounts
// them after `requestIdleCallback` (or earlier on user interaction) so they
// don't compete with the hero LCP image for bandwidth on first paint.
import DeferredAuthProviders from "@/components/DeferredAuthProviders";

// PageLoader is no longer used as the top-level Suspense fallback because it
// would replace the ENTIRE app (including Header) while route chunks load,
// pushing the LCP element (header logo) behind a multi-second render delay.
// Instead, the top-level Suspense uses a null fallback so the LocaleLayout
// shell (Header + chrome) renders immediately, and per-route Suspense inside
// LocaleLayout handles the content-area loading state.

const App = () => (
  <ErrorBoundary>
    <DeferredUI>
      {/* v7_startTransition lets React keep the previous page visible while the
          next route chunk loads (instead of unmounting it under the Suspense
          fallback). Dramatic improvement in perceived navigation speed.
          Zero runtime cost — just a behavior flag. */}
      <BrowserRouter future={{ v7_startTransition: true }}>
        <ScrollToTop />
        <Suspense fallback={null}>
          <DeferredAuthProviders>
            <Routes>
              {/* Root "/" renders Czech content directly (no redirect).
                  Canonical link in index.html points to /cs so SEO consolidates there.
                  This avoids NO_FCP errors in PageSpeed Insights / Lighthouse caused by
                  a client-side redirect on the root URL. */}
              <Route path="/" element={<LocaleLayout />}>
                <Route index element={<Index />} />
              </Route>

              {/* Legacy root paths → redirect to /cs/... */}
              <Route path="/login" element={<Navigate to="/cs/login" replace />} />
              <Route path="/signup" element={<Navigate to="/cs/signup" replace />} />
              <Route path="/reset-password" element={<Navigate to="/cs/reset-password" replace />} />
              <Route path="/profile" element={<Navigate to="/cs/profile" replace />} />

              {/* Legacy Webnode URLs → redirect to new structure */}
              <Route path="/o-mne" element={<Navigate to="/cs/about" replace />} />
              <Route path="/o-mne/" element={<Navigate to="/cs/about" replace />} />
              <Route path="/uspechy" element={<Navigate to="/cs/achievements" replace />} />
              <Route path="/uspechy/" element={<Navigate to="/cs/achievements" replace />} />
              <Route path="/muj-chov" element={<Navigate to="/cs" replace />} />
              <Route path="/muj-chov/" element={<Navigate to="/cs" replace />} />
              <Route path="/fotogalerie" element={<Navigate to="/cs" replace />} />
              <Route path="/fotogalerie/" element={<Navigate to="/cs" replace />} />
              <Route path="/nabidka" element={<Navigate to="/cs/sales" replace />} />
              <Route path="/nabidka/" element={<Navigate to="/cs/sales" replace />} />
              <Route path="/kontakt" element={<Navigate to="/cs/sales" replace />} />
              <Route path="/kontakt/" element={<Navigate to="/cs/sales" replace />} />
              <Route path="/l/*" element={<Navigate to="/cs" replace />} />

              {/* English legacy URLs (chovstrach.com) */}
              <Route path="/about-me" element={<Navigate to="/en/about" replace />} />
              <Route path="/about-me/" element={<Navigate to="/en/about" replace />} />
              <Route path="/for-sale" element={<Navigate to="/en/sales" replace />} />
              <Route path="/for-sale/" element={<Navigate to="/en/sales" replace />} />
              <Route path="/achievements" element={<Navigate to="/en/achievements" replace />} />
              <Route path="/achievements/" element={<Navigate to="/en/achievements" replace />} />
              <Route path="/my-breeding" element={<Navigate to="/en" replace />} />
              <Route path="/my-breeding/" element={<Navigate to="/en" replace />} />
              <Route path="/photogallery" element={<Navigate to="/en" replace />} />
              <Route path="/photogallery/" element={<Navigate to="/en" replace />} />
              <Route path="/contact" element={<Navigate to="/en/sales" replace />} />
              <Route path="/contact/" element={<Navigate to="/en/sales" replace />} />

              {/* Email unsubscribe (language-independent, in CS) */}
              <Route path="/unsubscribe" element={<UnsubscribePage />} />

              {/* Admin routes (language-independent) */}
              <Route
                path="/admin/*"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<AdminDashboard />} />
                <Route path="articles" element={<AdminArticles />} />
                <Route path="partners" element={<AdminPartners />} />
                <Route path="media" element={<AdminMedia />} />
                <Route path="achievements" element={<AdminAchievements />} />
                <Route path="lectures" element={<AdminLectures />} />
                <Route path="categories" element={<AdminCategories />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="inquiries" element={<AdminInquiries />} />
                <Route path="inquiries-campaign" element={<AdminInquiryCampaign />} />
                <Route path="popup-analytics" element={<AdminPopupAnalytics />} />
                <Route path="emails" element={<AdminEmailDeliverability />} />
                <Route path="campaigns" element={<AdminCampaigns />} />
                <Route path="analytics-check" element={<AdminAnalyticsCheck />} />
              </Route>

              {/* Locale-prefixed routes */}
              <Route path="/:lang" element={<LocaleLayout />}>
                <Route index element={<Index />} />
                <Route path="about" element={<AboutPage />} />
                <Route path="achievements" element={<AchievementsPage />} />
                <Route path="blog" element={<BlogPage />} />
                <Route path="blog/:slug" element={<BlogArticlePage />} />
                <Route path="lectures" element={<LecturesPage />} />
                <Route path="partners" element={<PartnersPage />} />
                <Route path="zebbie" element={<ZebbiePage />} />
                <Route path="sales" element={<SalesPage />} />
                <Route path="community" element={<CommunityPage />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="login" element={<LoginPage />} />
                <Route path="signup" element={<SignupPage />} />
                <Route path="reset-password" element={<ResetPasswordPage />} />
                <Route path="privacy" element={<PrivacyPage />} />
              </Route>

              <Route path="*" element={<NotFound />} />
            </Routes>
          </DeferredAuthProviders>
        </Suspense>
      </BrowserRouter>
    </DeferredUI>
  </ErrorBoundary>
);

export default App;
